import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Fade,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Popover,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import FilterListIcon from '@material-ui/icons/FilterList';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ClearIcon from '@material-ui/icons/Clear';
import LayersIcon from '@material-ui/icons/Layers';
import InboxIcon from '@material-ui/icons/Inbox';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import DecorpotLoader, { useDecorpotLoader } from '../../services/decorpotLoader';
import HttpService from '../../services/httpService';


ChartJS.register(ArcElement);

const BRAND_COLOR = '#f47d44';
const BRAND_DARK = '#d95f22';
const BRAND_SOFT = 'rgba(244, 125, 68, 0.12)';
const INK = '#20283a';
const MUTED = '#6b7385';


const STAGES = {
    NOT_TRIGGERED: 'Not Triggered',
    TRIGGER_PENDING: 'Trigger Pending',
    AWAITING_APPROVAL: 'Awaiting Approval',
    APPROVED: 'Approved',
    NOT_APPROVED: 'Not Approved',
    SENT_TO_VENDOR: 'Sent to Vendor',
};

const TONES = {
    slate: { bg: '#eef1f6', color: '#4b5568', border: '#dde3ec' },
    emerald: { bg: '#e7f7ef', color: '#0a7d57', border: '#bce6d3' },
    amber: { bg: '#fff5e0', color: '#96600a', border: '#ffe1a8' },
    rose: { bg: '#ffecec', color: '#c62b2b', border: '#ffcccc' },
    indigo: { bg: '#eaefff', color: '#33509e', border: '#ccd7fb' },
    brand: { bg: '#fff0e6', color: '#c4570f', border: '#ffd5bb' },
};

const STAGE_COLORS = {
    [STAGES.NOT_TRIGGERED]: TONES.slate,
    [STAGES.TRIGGER_PENDING]: TONES.brand,
    [STAGES.AWAITING_APPROVAL]: TONES.amber,
    [STAGES.APPROVED]: TONES.emerald,
    [STAGES.NOT_APPROVED]: TONES.rose,
    [STAGES.SENT_TO_VENDOR]: TONES.indigo,
};

const PO_STATUS_COLORS = {
    Approved: TONES.emerald,
    'Not Approved': TONES.rose,
    Pending: TONES.amber,
};

const buildColumns = (months) => [
    { id: 'slNo', label: 'Sl No', type: 'index', width: 64, align: 'center', sortable: false, sticky: true, stickyLeft: 0 },
    { id: 'itemName', label: 'Item Name', type: 'text', width: 250, align: 'left', sticky: true, stickyLeft: 64, lastSticky: true },
    { id: 'availableStock', label: 'Currently Available Stock', note: 'Live factory stock available in the system right now', type: 'number', width: 150, align: 'center' },
    {
        id: 'moq',
        label: 'MOQ',
        note: months.length
            ? 'Average of the last ' + months.length + ' completed months (' + months.map((m) => m.label).join(', ') + ') — click a value for the month-wise split'
            : 'Last 3 completed months’ average qty outwarded (e.g. for Sep take Jun, Jul & Aug)',
        type: 'moq',
        width: 130,
        align: 'center',
        tone: 'green',
    },
    { id: 'shippingTat', label: 'Shipping TAT (days)', note: 'Shipping TAT in days held against this item (moqTaTDays) — click the value to type a new number of days', type: 'shippingTat', width: 150, align: 'center', tone: 'green' },
    { id: 'triggerQty', label: 'Qty to Trigger Open PO', note: 'Ready when (Total Avl + Ordered Qty) ≤ (Qty to be Compared), otherwise Not Ready — hover or click the badge for the same formula filled in with this item’s values', type: 'triggerReady', width: 150, align: 'center', tone: 'yellow', sortKey: 'triggerReady' },
    { id: 'pendingApprovalQty', label: 'Open PO Pending with Kanishka', note: 'Qty across every open PO line of this item that is still “Sent for Approval”, skipping POs and lines already Finance Approved & Inwarded or Rejected — updates dynamically', type: 'openPoQty', qtyKey: 'pendingApprovalQty', linesKey: 'pendingPoLines', tipLabel: 'pending approval', width: 155, align: 'center' },
    { id: 'pendingVendorQty', label: 'Open PO Pending with Vendor', note: 'Qty across every open PO line of this item that is already “Approved” — the PO is with the vendor and the qty is still to be inwarded; POs and lines already Finance Approved & Inwarded or Rejected are skipped', type: 'openPoQty', qtyKey: 'pendingVendorQty', linesKey: 'vendorPoLines', tipLabel: 'pending with vendor', width: 155, align: 'center' },
    { id: 'totalAvailableQty', label: 'Total Avl + Ordered Qty', note: 'Currently available stock + open PO pending with Kanishka + open PO pending with vendor — click the value for the breakdown', type: 'totalAvailableQty', width: 150, align: 'center', tone: 'yellow' },
    { id: 'compareQty', label: 'Qty to be Compared', note: 'MOQ + (MOQ × shipping TAT ÷ 30) — one MOQ plus whatever the shipping TAT consumes, a month counted as 30 days; click the value for the breakdown', type: 'compareQty', width: 145, align: 'center', tone: 'cyan' },
    { id: 'triggerStatus', label: 'Trigger Status', note: 'When the automation last raised an Open PO for this item. Stays until that PO is Delivered (or Rejected), then clears — click for the PO', type: 'triggerStatus', width: 165, align: 'center', tone: 'orange', sortKey: 'triggeredAt' },
    // { id: 'triggerDateTime', label: 'Trigger Date & Time', note: 'When the system triggered the open PO', type: 'datetime', width: 185, align: 'center', tone: 'cream', pending: true },
    // { id: 'poStatus', label: 'Approve / Not Approve', note: 'Open PO status after Kanishka’s review', type: 'poStatus', width: 170, align: 'center', tone: 'pink', pending: true },
    // { id: 'sentToVendorDate', label: 'Date Sent to Vendor', note: 'Date the approved open PO was sent to the vendor', type: 'date', width: 165, align: 'center', tone: 'blue', pending: true },
    // { id: 'stage', label: 'Stage', note: 'Derived from trigger → approval → dispatch', type: 'stage', width: 175, align: 'center', tone: 'brand', pending: true },
];

const TONE_STYLES = {
    green: { bg: '#edf7f3' },
    yellow: { bg: '#fff8e3' },
    cyan: { bg: '#e7f6f9' },
    orange: { bg: '#fdf0e9' },
    cream: { bg: '#fffbee' },
    pink: { bg: '#fdf0f5' },
    blue: { bg: '#eef3fc' },
    brand: { bg: '#fef1ea' },
};


const buildRow = (row, months) => {
    const availableStock = Number(row.availableStock) || 0;
    const moq = Number(row.moq) || 0;
    const monthly = row.monthlyOutwardedQty || {};
    const flatMonths = {};
    months.forEach((month) => {
        flatMonths['month_' + month.key] = Number(monthly[month.key]) || 0;
    });

    return {
        ...row,
        ...flatMonths,
        availableStock,
        moq,
        totalOutwardedQty: Number(row.totalOutwardedQty) || 0,
        definedMoq: Number(row.definedMoq) || 0,
        pendingApprovalQty: Number(row.pendingApprovalQty) || 0,
        pendingPoLines: Array.isArray(row.pendingPoLines) ? row.pendingPoLines : [],
        pendingVendorQty: Number(row.pendingVendorQty) || 0,
        vendorPoLines: Array.isArray(row.vendorPoLines) ? row.vendorPoLines : [],
        openPo: row.openPo || null,
        shippingTat: Number(row.shippingTat) || 0,
        totalAvailableQty: Number(row.totalAvailableQty) || 0,
        compareQty: Number(row.compareQty) || 0,
        triggerReady: Boolean(row.triggerReady),
        triggeredAt: row.triggeredAt || null,
        triggerOrderId: row.triggerOrderId || '',
        triggerPoStatus: row.triggerPoStatus || '',
        belowMoq: moq > 0 && availableStock <= moq,
    };
};


const SHEET_TYPE = 'factory';
const SHEET_MONTHS = 3;

const MAX_TAT_DAYS = 3650;

const TOP_MATERIALS_COUNT = 10;
const DONUT_SLICE_COUNT = 5;

const SLICE_COLORS = ['#2a78d6', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const OTHER_COLOR = '#c7ccd6';
const OPEN_PO_TIP_LIMIT = 6;
const ALL_ROWS = -1;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 150, 200, { label: 'All', value: ALL_ROWS }];
const DEFAULT_ROWS_PER_PAGE = ALL_ROWS;

const displayedRowsLabel = (rowsPerPage) => ({ from, to, count }) => (
    rowsPerPage === ALL_ROWS
        ? (count ? 1 : 0) + '-' + count + ' of ' + count
        : from + '-' + to + ' of ' + count
);

const isValidTatDraft = (draft) => {
    if (!/^\d+$/.test(draft)) return false;
    const days = Number(draft);
    return Number.isInteger(days) && days >= 0 && days <= MAX_TAT_DAYS;
};

const formatQty = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const formatDateTime = (value) => (value ? moment(value).format('DD MMM YYYY, hh:mm A') : '—');
const formatDate = (value) => (value ? moment(value).format('DD MMM YYYY') : '—');

const escapeCsv = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
};


const sortKeyOf = (col) => col.sortKey || col.id;

const comparator = (a, b, orderBy) => {
    const left = a[orderBy];
    const right = b[orderBy];
    if (left === right) return 0;
    if (left === null || left === undefined || left === '') return 1;
    if (right === null || right === undefined || right === '') return -1;
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    if (typeof left === 'boolean' && typeof right === 'boolean') return (left ? 1 : 0) - (right ? 1 : 0);
    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
};

const VIEWS = {
    ALL: 'All Items',
    BELOW_MOQ: 'Below MOQ',
    OUTWARDED: 'Outwarded in last 3 months',
    ZERO_STOCK: 'Zero Stock',
    ZERO_MOQ: 'MOQ Zero Value',
};
const VIEW_LIST = Object.values(VIEWS);

const DEFAULT_FILTERS = { search: '', view: VIEWS.ALL };
const BOOT_DELAY = 600;


const useStyles = makeStyles((theme) => ({
    '@keyframes moqRise': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'none' } },
    '@keyframes moqFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },

    page: {
        padding: theme.spacing(3.5),
        background: 'linear-gradient(180deg, #f6f7fb 0%, #ffffff 320px)',
        minHeight: '100%',
        [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
    },
    rise: { animation: '$moqRise .45s cubic-bezier(.22,.8,.3,1) both' },
    fadeUp: { animation: '$moqFadeIn .45s ease both' },

    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(3),
    },
    titleWrap: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.75), minWidth: 0 },
    titleIcon: {
        width: 54,
        height: 54,
        borderRadius: 16,
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(135deg, ' + BRAND_COLOR + ' 0%, ' + BRAND_DARK + ' 100%)',
        color: '#fff',
        boxShadow: '0 14px 26px -12px rgba(244, 125, 68, 0.95)',
        flexShrink: 0,
    },
    title: {
        fontSize: 27,
        fontWeight: 800,
        color: INK,
        lineHeight: 1.15,
        letterSpacing: -0.3,
        [theme.breakpoints.down('xs')]: { fontSize: 21 },
    },
    headerActions: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap' },

    /* ---------- buttons ---------- */
    primaryBtn: {
        textTransform: 'none',
        fontSize: 15.5,
        fontWeight: 700,
        borderRadius: 13,
        padding: '12px 28px',
        color: '#fff',
        background: 'linear-gradient(135deg, ' + BRAND_COLOR + ' 0%, ' + BRAND_DARK + ' 100%)',
        boxShadow: '0 12px 22px -12px rgba(244, 125, 68, 0.95)',
        transition: 'transform .18s ease, box-shadow .18s ease, filter .18s ease',
        '&:hover': { filter: 'brightness(1.04)', transform: 'translateY(-1px)', boxShadow: '0 16px 26px -12px rgba(244, 125, 68, 1)' },
        '&:active': { transform: 'translateY(0)' },
    },
    ghostBtn: {
        textTransform: 'none',
        fontSize: 15.5,
        fontWeight: 700,
        borderRadius: 13,
        padding: '12px 26px',
        color: BRAND_DARK,
        border: '1.5px solid rgba(244, 125, 68, 0.45)',
        backgroundColor: '#fff',
        transition: 'background-color .18s ease, border-color .18s ease, transform .18s ease',
        '&:hover': { backgroundColor: BRAND_SOFT, borderColor: BRAND_COLOR, transform: 'translateY(-1px)' },
    },

    /* ---------- most-used materials ---------- */
    usageCard: {
        borderRadius: 18,
        border: '1px solid #ebeef4',
        background: '#fff',
        padding: theme.spacing(2.25, 2.5),
        boxShadow: '0 18px 40px -32px rgba(23, 31, 56, 0.55)',
    },
    usageHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        cursor: 'pointer',
        userSelect: 'none',
        borderRadius: 12,
        margin: theme.spacing(-0.75, -1),
        padding: theme.spacing(0.75, 1),
        transition: 'background .16s ease',
        '&:hover': { background: '#fafbfd' },
    },
    /* only spaced off the body once there is a body below it */
    usageHeadOpen: { marginBottom: theme.spacing(1.5) },
    usageHeadRight: { display: 'flex', alignItems: 'center', gap: 10 },
    usageChevron: {
        color: '#9aa1b1',
        fontSize: 24,
        transition: 'transform .22s ease, color .16s ease',
        flexShrink: 0,
    },
    usageChevronOpen: { transform: 'rotate(180deg)', color: BRAND_DARK },
    usageTitleWrap: { display: 'flex', alignItems: 'center', gap: 10 },
    usageTitle: { fontSize: 15.5, fontWeight: 800, color: INK, letterSpacing: 0.1 },
    usageSub: { fontSize: 12, fontWeight: 700, color: MUTED, marginTop: 2 },
    usageHint: { fontSize: 11.5, fontWeight: 700, color: '#9aa1b1' },

    /* the donut block: share of qty outwarded, total in the hole */
    usageHero: {
        borderRadius: 14,
        background: '#fbfcfe',
        border: '1px solid #eef1f6',
        padding: theme.spacing(2, 1.5),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    donutWrap: { position: 'relative', width: 168, height: 168 },
    /* the hole — pointer-events off so it never eats a slice hover */
    donutCentre: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        textAlign: 'center',
        padding: '0 26px',
    },
    donutCentreValue: { fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1.1, letterSpacing: -0.3 },
    donutCentreLabel: {
        fontSize: 8.5,
        fontWeight: 800,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginTop: 3,
        lineHeight: 1.3,
    },
    /* fixed height so swapping the default share for a hovered slice never
       nudges anything below it */
    usageHeroFoot: {
        marginTop: 14,
        paddingTop: 12,
        borderTop: '1px dashed #e4e8f0',
        width: '100%',
        textAlign: 'center',
        height: 62,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    donutDetailName: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        maxWidth: '100%',
        minWidth: 0,
    },
    donutDetailText: {
        fontSize: 12.5,
        fontWeight: 800,
        color: INK,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    donutDetailQty: {
        marginTop: 5,
        fontSize: 15,
        fontWeight: 800,
        color: INK,
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
    },
    donutDetailPct: { fontSize: 12, fontWeight: 800, color: BRAND_DARK },
    usageHeroShare: { fontSize: 17, fontWeight: 800, color: BRAND_DARK, lineHeight: 1.1 },
    usageHeroLabel: { fontSize: 10.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 4 },

    /* swatch tying a list row to its donut slice */
    usageSwatch: {
        display: 'block',
        width: 9,
        height: 9,
        borderRadius: 3,
        flex: '0 0 auto',
    },
    usageNameWrap: { display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 },

    /* one ranked row: rank, name, bar, qty, share */
    usageRow: {
        display: 'grid',
        gridTemplateColumns: '26px minmax(110px, 1.25fr) minmax(70px, 2fr) 74px 52px',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        width: '100%',
        padding: '5px 6px',
        borderRadius: 9,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background .16s ease',
        '&:hover': { background: '#fafbfd' },
    },
    /* the row(s) the hovered donut slice stands for */
    /* Trigger Status pill — an item with a live automation PO against it */
    triggeredBadge: {
        display: 'inline-block',
        padding: theme.spacing(0.5, 1.1),
        borderRadius: 9,
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1.35,
        color: '#b45309',
        background: 'rgba(244, 125, 68, 0.13)',
        border: '1px solid rgba(244, 125, 68, 0.3)',
        cursor: 'pointer',
        transition: 'filter .16s ease',
        '&:hover': { filter: 'brightness(0.97)' },
    },
    usageRowLit: { background: '#f4f7fc' },
    usageRowDim: { opacity: 0.45 },
    usageRank: {
        fontSize: 11,
        fontWeight: 800,
        color: MUTED,
        width: 22,
        height: 22,
        borderRadius: 7,
        background: '#f3f5f9',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    usageRankTop: { background: BRAND_SOFT, color: BRAND_DARK },
    usageName: {
        fontSize: 12.5,
        fontWeight: 800,
        color: INK,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    /* bar length is the whole encoding, so every bar takes one hue */
    usageTrack: { display: 'block', height: 9, borderRadius: 999, background: '#f1f3f8', overflow: 'hidden' },
    usageFill: {
        display: 'block',
        height: '100%',
        borderRadius: 999,
        background: BRAND_COLOR,
        transition: 'width .5s cubic-bezier(.4,0,.2,1)',
    },
    usageQty: { fontSize: 13, fontWeight: 800, color: INK, textAlign: 'right', whiteSpace: 'nowrap' },
    usageShare: { fontSize: 11.5, fontWeight: 700, color: MUTED, textAlign: 'right', whiteSpace: 'nowrap' },
    usageEmpty: { fontSize: 12.5, fontWeight: 700, color: MUTED, padding: theme.spacing(2, 0) },

    /* ---------- kpi ---------- */
    kpiCard: {
        position: 'relative',
        overflow: 'hidden',
        padding: theme.spacing(2.25, 2.5),
        [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.75, 1.75), gap: theme.spacing(1.25) },
        borderRadius: 18,
        border: '1px solid #ebeef4',
        boxShadow: '0 14px 34px -26px rgba(23, 31, 56, 0.6)',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        height: '100%',
        transition: 'transform .2s cubic-bezier(.22,.8,.3,1), box-shadow .2s ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 22px 40px -24px rgba(23, 31, 56, 0.7)' },
        '&:after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: 'var(--accent)',
        },
    },
    kpiIcon: {
        width: 48,
        height: 48,
        borderRadius: 14,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        [theme.breakpoints.down('xs')]: { width: 42, height: 42, borderRadius: 12 },
    },
    kpiValue: {
        fontSize: 28,
        fontWeight: 800,
        color: INK,
        lineHeight: 1.05,
        letterSpacing: -0.5,
        [theme.breakpoints.down('xs')]: { fontSize: 24 },
    },
    kpiLabel: {
        fontSize: 12.5,
        color: MUTED,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        marginTop: 3,
        [theme.breakpoints.down('xs')]: { fontSize: 11, letterSpacing: 0.2 },
    },

    /* ---------- filters ---------- */
    filterCard: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2.5),
        borderRadius: 18,
        border: '1px solid #ebeef4',
        boxShadow: '0 14px 34px -28px rgba(23, 31, 56, 0.65)',
    },
    filterHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.25), marginBottom: theme.spacing(2) },
    filterHeadText: { fontSize: 15, fontWeight: 800, color: INK, letterSpacing: 0.2 },
    field: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#fbfcfe',
            transition: 'box-shadow .18s ease, background-color .18s ease',
            '& fieldset': { borderColor: '#e2e6ee' },
            '&:hover fieldset': { borderColor: 'rgba(244, 125, 68, 0.55)' },
            '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 4px rgba(244, 125, 68, 0.12)' },
            '&.Mui-focused fieldset': { borderColor: BRAND_COLOR, borderWidth: 1.5 },
        },
        '& .MuiInputLabel-outlined': { fontSize: 14.5 },
        '& .MuiInputLabel-outlined.Mui-focused': { color: BRAND_DARK, fontWeight: 600 },
        '& .MuiOutlinedInput-input': { fontSize: 14.5, fontWeight: 600, color: INK, padding: '13.5px 14px' },
        '& .MuiSelect-select': { fontSize: 14.5, fontWeight: 600 },
    },
    adornIcon: { color: MUTED, fontSize: 21 },
    activeChips: { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: theme.spacing(2) },
    activeChip: {
        height: 30,
        fontSize: 12.5,
        fontWeight: 700,
        color: BRAND_DARK,
        backgroundColor: BRAND_SOFT,
        transition: 'background-color .18s ease',
        '&:hover': { backgroundColor: 'rgba(244, 125, 68, 0.2)' },
        '& .MuiChip-deleteIcon': { color: BRAND_DARK, fontSize: 17, '&:hover': { color: BRAND_COLOR } },
    },

    /* ---------- table ---------- */
    tableCard: {
        marginTop: theme.spacing(3),
        borderRadius: 18,
        border: '1px solid #ebeef4',
        boxShadow: '0 18px 44px -32px rgba(23, 31, 56, 0.7)',
        overflow: 'hidden',
    },
    tableToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 2.5),
        borderBottom: '1px solid #eef0f5',
        flexWrap: 'wrap',
    },
    tableTitle: { fontSize: 16, fontWeight: 800, color: INK, letterSpacing: -0.2 },
    countPill: {
        fontSize: 12.5,
        fontWeight: 800,
        color: BRAND_DARK,
        backgroundColor: BRAND_SOFT,
        borderRadius: 20,
        padding: '5px 12px',
    },
    toolbarHint: { fontSize: 12.5, color: MUTED, fontWeight: 600 },
    tableWrap: { overflow: 'auto', maxHeight: 'calc(100vh - 180px)', minHeight: 320 },
    table: { borderCollapse: 'separate', borderSpacing: 0 },
    headCell: {
        top: 0,
        padding: '15px 12px',
        borderBottom: '2px solid #dfe3ec',
        borderRight: '1px solid #dbe0ea',
        backgroundColor: '#fbfcfe',
        cursor: 'default',
    },
    headInner: { display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' },
    headLabel: { fontSize: 15.5, fontWeight: 800, color: INK, lineHeight: 1.3, whiteSpace: 'normal', letterSpacing: 0.1 },
    headInfo: {
        fontSize: 15,
        color: '#b4bac6',
        flexShrink: 0,
        opacity: 0.85,
        transition: 'color .18s ease, opacity .18s ease',
    },
    headCellHover: { '&:hover $headInfo': { color: BRAND_COLOR, opacity: 1 } },
    sortLabel: {
        '&.MuiTableSortLabel-active': { color: BRAND_DARK },
        '& .MuiTableSortLabel-icon': { fontSize: 17 },
        '&:hover': { color: BRAND_DARK },
    },
    stickyHeadCell: { '&.MuiTableCell-root': { zIndex: 6 } },
    bodyRow: {
        animation: '$moqFadeIn .35s ease both',
        transition: 'background-color .16s ease',
        '&:nth-of-type(even)': { backgroundColor: '#fcfcfe' },
        '&:nth-of-type(even) $stickyCell': { backgroundColor: '#fcfcfe' },
        '&:hover': { backgroundColor: '#fef6f1' },
        '&:hover $stickyCell': { backgroundColor: '#fef6f1' },
    },
    bodyCell: {
        padding: '14px 12px',
        fontSize: 14,
        fontWeight: 700,
        color: INK,
        borderBottom: '1px solid #f1f3f8',
        borderRight: '1px solid #e6eaf2',
        whiteSpace: 'nowrap',
    },
    stickyCell: {
        '&.MuiTableCell-root': { position: 'sticky' },
        backgroundColor: '#fff',
        zIndex: 1,
        verticalAlign: 'middle',
        boxShadow: 'inset -1px 0 0 #e6eaf2',
    },
    stickyEdge: { boxShadow: 'inset -1px 0 0 #dbe0ea, 8px 0 14px -10px rgba(23, 31, 56, 0.28)' },
    serial: { fontSize: 13.5, fontWeight: 700, color: '#9aa1b1' },
    itemCell: { minWidth: 0, maxWidth: '100%' },
    itemName: {
        fontWeight: 800,
        fontSize: 14.5,
        color: INK,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    itemMeta: {
        fontSize: 12,
        color: MUTED,
        marginTop: 3,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    /* cards have the full width to themselves, so they wrap instead */
    mItemName: { fontWeight: 800, fontSize: 14.5, color: INK, lineHeight: 1.3, whiteSpace: 'normal' },
    mItemMeta: { fontSize: 12, color: MUTED, marginTop: 3, fontWeight: 600, whiteSpace: 'normal' },
    numStrong: { fontWeight: 800, fontSize: 14.5 },
    moqValue: {
        fontWeight: 800,
        fontSize: 14.5,
        color: INK,
        cursor: 'pointer',
        borderBottom: '1px dashed rgba(244, 125, 68, 0.75)',
        transition: 'color .16s ease',
        '&:hover': { color: BRAND_DARK },
    },
    moqPopoverPaper: {
        borderRadius: 14,
        border: '1px solid #ebeef4',
        boxShadow: '0 20px 44px -24px rgba(23, 31, 56, 0.8)',
        marginTop: 6,
    },
    lowStock: { color: '#c62828', fontWeight: 800, fontSize: 14.5 },

    readyBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: theme.spacing(0.4, 1.1),
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 800,
        letterSpacing: 0.2,
        cursor: 'pointer',
        border: '1px solid transparent',
        transition: 'filter .16s ease',
        '&:hover': { filter: 'brightness(0.96)' },
        '& svg': { fontSize: 15 },
    },
    readyYes: { color: '#0f7a52', background: 'rgba(15, 138, 95, 0.12)', borderColor: 'rgba(15, 138, 95, 0.28)' },
    readyNo: { color: '#6b7385', background: 'rgba(107, 115, 133, 0.10)', borderColor: 'rgba(107, 115, 133, 0.22)' },


    /* ---------- shipping TAT / open PO cells ---------- */
    tatValue: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 48,
        height: 30,
        padding: '0 10px',
        borderRadius: 9,
        fontSize: 14.5,
        fontWeight: 800,
        color: INK,
        cursor: 'pointer',
        backgroundColor: '#fff',
        border: '1px dashed #ccd3e0',
        transition: 'border-color .16s ease, background-color .16s ease, color .16s ease',
        '&:hover': { borderColor: BRAND_COLOR, backgroundColor: BRAND_SOFT, color: BRAND_DARK },
        '&:focus': { outline: 'none', borderColor: BRAND_COLOR, boxShadow: '0 0 0 3px rgba(244, 125, 68, 0.14)' },
    },
    tatEmpty: { color: '#aab0bd' },
    tatPopover: { padding: theme.spacing(1.75, 2), width: 250 },
    tatPopoverTitle: {
        fontSize: 10.5,
        fontWeight: 800,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    tatPopoverMeta: { fontSize: 12, fontWeight: 700, color: '#5b6478', marginTop: 4, marginBottom: 10 },
    tatInput: {
        width: '100%',
        height: 36,
        boxSizing: 'border-box',
        borderRadius: 10,
        border: '1.5px solid #dfe3ec',
        backgroundColor: '#fbfcfe',
        padding: '0 10px',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        color: INK,
        outline: 'none',
        transition: 'border-color .16s ease, box-shadow .16s ease',
        '&:focus': { borderColor: BRAND_COLOR, backgroundColor: '#fff', boxShadow: '0 0 0 3px rgba(244, 125, 68, 0.14)' },
    },
    tatPreview: { marginTop: 9, fontSize: 12.5, fontWeight: 700, color: MUTED, minHeight: 18 },
    tatPreviewDays: { color: '#0f8a5f', fontWeight: 800 },
    tatActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
    tatCancel: {
        height: 32,
        padding: '0 12px',
        borderRadius: 9,
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 800,
        color: MUTED,
        cursor: 'pointer',
        backgroundColor: '#fff',
        border: '1.5px solid #e2e6ee',
        transition: 'background-color .16s ease',
        '&:hover': { backgroundColor: '#f4f6fa' },
    },
    tatSave: {
        height: 32,
        padding: '0 16px',
        borderRadius: 9,
        border: 'none',
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 800,
        letterSpacing: 0.2,
        color: '#fff',
        cursor: 'pointer',
        background: 'linear-gradient(135deg, ' + BRAND_COLOR + ' 0%, ' + BRAND_DARK + ' 100%)',
        boxShadow: '0 8px 16px -10px rgba(244, 125, 68, 1)',
        transition: 'filter .16s ease',
        '&:hover': { filter: 'brightness(1.05)' },
        /* stays locked until a future date is picked */
        '&:disabled': { background: '#e7eaf1', color: '#a9afbd', cursor: 'not-allowed', boxShadow: 'none' },
    },
    pendingQty: { fontWeight: 800, fontSize: 14.5, color: INK, borderBottom: '1px dotted #c3c9d6', cursor: 'help' },


    tipCard: {
        background: '#fff',
        color: INK,
        padding: 0,
        margin: 0,
        maxWidth: 'none',
        borderRadius: 14,
        border: '1px solid #ebeef4',
        boxShadow: '0 20px 44px -24px rgba(23, 31, 56, 0.8)',
    },
    tipArrow: {
        color: '#fff',
        '&::before': { border: '1px solid #ebeef4' },
    },
    panel: { padding: theme.spacing(1.5, 1.75), minWidth: 210 },
    /* a one-line hint with no table behind it */
    tipText: {
        padding: theme.spacing(1, 1.25),
        fontSize: 12,
        fontWeight: 700,
        color: MUTED,
        maxWidth: 260,
        lineHeight: 1.5,
    },
    tipTextStrong: { color: INK, fontWeight: 800 },
    panelTitle: {
        fontSize: 13.5,
        fontWeight: 800,
        color: INK,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        marginBottom: 10,
    },
    panelTable: { width: '100%', borderCollapse: 'collapse' },
    panelTh: {
        fontSize: 11.5,
        fontWeight: 800,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        textAlign: 'left',
        padding: '0 10px 6px 0',
        borderBottom: '1px solid #ebeef4',
        whiteSpace: 'nowrap',
    },
    panelThNum: { textAlign: 'right', paddingLeft: theme.spacing(2) },
    panelTd: {
        fontSize: 12.5,
        fontWeight: 700,
        color: MUTED,
        padding: '6px 10px 6px 0',
        borderBottom: '1px solid #f4f6fa',
        whiteSpace: 'nowrap',
    },
    panelTdNum: {
        textAlign: 'right',
        paddingLeft: theme.spacing(2),
        color: INK,
        fontWeight: 800,
        fontSize: 13.5,
    },
    /* rule between columns — every cell but the last one in its row */
    panelDivider: { borderRight: '1px solid #ebeef4', paddingRight: 10 },
    /* the formula block — bold, and set off from the table above it */
    panelFormula: {
        marginTop: 10,
        paddingTop: 8,
        borderTop: '1px dashed #e7eaf1',
        fontSize: 12,
        fontWeight: 800,
        color: MUTED,
    },
    panelFormulaLabel: {
        fontSize: 10,
        fontWeight: 800,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 4,
    },
    panelExpr: { color: INK, fontWeight: 800, fontSize: 12.5 },
    panelSubbed: { marginTop: 5, fontWeight: 800 },
    panelResult: { color: '#0f8a5f', fontWeight: 800, fontSize: 13.5 },
    panelResultNo: { color: '#6b7385', fontWeight: 800, fontSize: 13.5 },
    panelFoot: { marginTop: 7, fontSize: 11.5, fontWeight: 700, color: MUTED, lineHeight: 1.45 },
    panelVerdict: {
        marginTop: 9,
        paddingTop: 8,
        borderTop: '1px dashed #e7eaf1',
        fontSize: 12.5,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        '& svg': { fontSize: 16 },
    },
    panelVerdictYes: { color: '#0f7a52' },
    panelVerdictNo: { color: '#6b7385' },
    dateText: { fontSize: 13.5, fontWeight: 600, color: '#414a5e' },
    statusChip: {
        height: 32,
        fontSize: 13,
        fontWeight: 800,
        borderRadius: 10,
        letterSpacing: 0.2,
        '& .MuiChip-label': { padding: '0 12px' },
    },
    stageChip: {
        height: 32,
        fontSize: 13,
        fontWeight: 800,
        borderRadius: 10,
        minWidth: 138,
        letterSpacing: 0.2,
        '& .MuiChip-label': { padding: '0 12px' },
    },
    chipInner: { display: 'inline-flex', alignItems: 'center', gap: 7 },
    chipDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
    muted: { color: '#aab0bd', fontWeight: 700 },
    pagination: {
        borderTop: '1px solid #eef0f5',
        '& .MuiTablePagination-caption': { fontSize: 13.5, color: MUTED, fontWeight: 600 },
        '& .MuiTablePagination-select': { fontSize: 13.5, fontWeight: 700 },
        '& .MuiTablePagination-toolbar': { minHeight: 60 },
    },

    /* ---------- empty ---------- */
    empty: { padding: theme.spacing(7, 2), textAlign: 'center' },
    emptyIcon: { fontSize: 54, color: '#d7dbe4' },
    emptyTitle: { fontSize: 16.5, fontWeight: 800, color: INK, marginTop: 10 },
    emptyText: { fontSize: 14, color: MUTED, marginTop: 6 },

    /* ---------- mobile cards ---------- */
    mCard: {
        padding: theme.spacing(2),
        borderRadius: 16,
        border: '1px solid #ebeef4',
        boxShadow: '0 12px 28px -26px rgba(23, 31, 56, 0.75)',
        marginBottom: theme.spacing(1.75),
        animation: '$moqRise .4s cubic-bezier(.22,.8,.3,1) both',
    },
    mHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
    mGrid: {
        marginTop: theme.spacing(1.75),
        paddingTop: theme.spacing(1.75),
        borderTop: '1px dashed #e7eaf1',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(1.75),
    },
    mLabel: { fontSize: 10.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 },
    mValue: { fontSize: 15, fontWeight: 800, color: INK, marginTop: 3 },
    mFoot: { marginTop: theme.spacing(1.75), display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
}));


const KpiCard = React.memo(({ icon, value, label, color, bg, classes, delay }) => (
    <Paper
        className={classes.kpiCard + ' ' + classes.rise}
        elevation={0}
        style={{ '--accent': color, animationDelay: delay }}
    >
        <div className={classes.kpiIcon} style={{ backgroundColor: bg, color }}>{icon}</div>
        <div style={{ minWidth: 0 }}>
            <div className={classes.kpiValue}>{value}</div>
            <div className={classes.kpiLabel}>{label}</div>
        </div>
    </Paper>
));

const StatusChip = React.memo(({ label, tone, classes, className }) => (
    <Chip
        size="small"
        className={className}
        style={{ backgroundColor: tone.bg, color: tone.color, border: '1px solid ' + tone.border }}
        label={
            <span className={classes.chipInner}>
                <span className={classes.chipDot} style={{ backgroundColor: tone.color }} />
                {label}
            </span>
        }
    />
));

const StageChip = React.memo(({ stage, classes, className }) => (
    <StatusChip label={stage} tone={STAGE_COLORS[stage] || TONES.slate} classes={classes} className={className} />
));

const TriggerChip = React.memo(({ value, classes, className }) => (
    <StatusChip label={value} tone={value === 'Yes' ? TONES.emerald : TONES.slate} classes={classes} className={className} />
));

const PoStatusChip = React.memo(({ value, classes, className }) => {
    if (!value) return <span className={classes.muted}>—</span>;
    return <StatusChip label={value} tone={PO_STATUS_COLORS[value] || TONES.slate} classes={classes} className={className} />;
});

const ExplainPanel = ({ title, children, classes }) => (
    <div className={classes.panel}>
        {title ? <div className={classes.panelTitle}>{title}</div> : null}
        {children}
    </div>
);

const PanelTable = ({ head, rows, classes }) => {
    const last = head.length - 1;
    const cellClass = (base, index) => base
        + (index ? ' ' + (base === classes.panelTh ? classes.panelThNum : classes.panelTdNum) : '')
        + (index < last ? ' ' + classes.panelDivider : '');

    return (
        <table className={classes.panelTable}>
            <thead>
                <tr>
                    {head.map((heading, index) => (
                        <th key={heading} className={cellClass(classes.panelTh, index)}>
                            {heading}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.key}>
                        {row.cells.map((cell, index) => (
                            <td key={index} className={cellClass(classes.panelTd, index)}>
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};


const PanelFormula = ({ label, expr, subbed, result, resultTone, classes }) => (
    <div className={classes.panelFormula}>
        <div className={classes.panelFormulaLabel}>{label || 'Formula'}</div>
        <div className={classes.panelExpr}>{expr}</div>
        {subbed ? (
            <div className={classes.panelSubbed}>
                <span className={classes.panelExpr}>{subbed}</span>
                {result !== undefined && result !== null ? (
                    <>
                        {' = '}
                        <span className={resultTone === 'no' ? classes.panelResultNo : classes.panelResult}>
                            {result}
                        </span>
                    </>
                ) : null}
            </div>
        ) : null}
    </div>
);


const ExplainTooltip = ({ title, classes, placement, children }) => (
    <Tooltip
        title={title}
        placement={placement || 'top'}
        enterDelay={200}
        arrow
        classes={{ tooltip: classes.tipCard, arrow: classes.tipArrow }}
    >
        {children}
    </Tooltip>
);



const ExplainValueCell = ({ value, panel, classes, badgeClass }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <span
                role="button"
                tabIndex={0}
                className={badgeClass || classes.moqValue}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                onKeyPress={(event) => { if (event.key === 'Enter') setAnchorEl(event.currentTarget); }}
            >
                {value}
            </span>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                classes={{ paper: classes.moqPopoverPaper }}
            >
                {panel}
            </Popover>
        </>
    );
};


const UsageDonut = ({ data, hoverIndex, onHoverSlice, classes }) => {
    const { slices, totalQty } = data;

    const chartData = {
        labels: slices.map((slice) => slice.label),
        datasets: [{
            data: slices.map((slice) => slice.qty),
            backgroundColor: slices.map((slice) => slice.color),
            borderColor: '#fff',
            borderWidth: 2,
            hoverBorderColor: '#fff',
            hoverBorderWidth: 2,
            hoverOffset: 6,
        }],
    };

    const options = {
        cutout: '66%',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 520 },
        onHover: (event, active) => {
            const next = active && active.length ? active[0].index : null;
            if (next !== hoverIndex) onHoverSlice(next);
        },
        plugins: {
            legend: { display: false },
        },
    };

    return (
        <div
            className={classes.donutWrap}
            // chart.js does not always fire onHover on the way out
            onMouseLeave={() => onHoverSlice(null)}
        >
            <Doughnut data={chartData} options={options} />
            <div className={classes.donutCentre}>
                <span className={classes.donutCentreValue}>{formatQty(totalQty)}</span>
                <span className={classes.donutCentreLabel}>Total qty outwarded</span>
            </div>
        </div>
    );
};



const UsageDonutDetail = ({ data, hoverIndex, classes }) => {
    const { slices, totalQty, topQty, top } = data;
    const slice = hoverIndex === null || hoverIndex === undefined ? null : slices[hoverIndex];
    const share = (qty) => (totalQty > 0 ? (qty / totalQty) * 100 : 0);

    if (!slice) {
        return (
            <div className={classes.usageHeroFoot}>
                <div className={classes.usageHeroShare}>{share(topQty).toFixed(1)}%</div>
                <div className={classes.usageHeroLabel}>Came from these {top.length}</div>
            </div>
        );
    }

    return (
        <div className={classes.usageHeroFoot}>
            <div className={classes.donutDetailName}>
                <span className={classes.usageSwatch} style={{ background: slice.color }} />
                <span className={classes.donutDetailText} title={slice.label}>{slice.label}</span>
            </div>
            <div className={classes.donutDetailQty}>
                {formatQty(slice.qty)}
                <span className={classes.donutDetailPct}>{share(slice.qty).toFixed(1)}%</span>
            </div>
        </div>
    );
};



const TopMaterialsCard = ({ data, months, classes }) => {
    const { top, totalQty, topQty } = data;
    const monthLabel = months.length ? months.length + ' months' : '3 months';
    const [hoverIndex, setHoverIndex] = useState(null);
    const [open, setOpen] = useState(false);

    const toggle = () => {
        setOpen((prev) => {
            if (prev) setHoverIndex(null);
            return !prev;
        });
    };

    if (!top.length) {
        return (
            <Paper className={classes.usageCard} elevation={0}>
                <div className={classes.usageTitleWrap}>
                    <InsertChartOutlinedIcon style={{ color: BRAND_COLOR, fontSize: 21 }} />
                    <div>
                        <div className={classes.usageTitle}>Most-Used Materials</div>
                        <div className={classes.usageSub}>Last {monthLabel} by qty outwarded</div>
                    </div>
                </div>
                <div className={classes.usageEmpty}>
                    No qty was outwarded in the last {monthLabel}, so there is nothing to rank yet.
                </div>
            </Paper>
        );
    }

    const peak = top[0].totalOutwardedQty || 1;
    const share = (qty) => (totalQty > 0 ? (qty / totalQty) * 100 : 0);
    const sliceColor = (index) => (index < DONUT_SLICE_COUNT ? SLICE_COLORS[index] : OTHER_COLOR);


    const rowTone = (index) => {
        if (hoverIndex === null) return '';
        const isOther = hoverIndex >= DONUT_SLICE_COUNT;
        const lit = isOther ? index >= DONUT_SLICE_COUNT : index === hoverIndex;
        return ' ' + (lit ? classes.usageRowLit : classes.usageRowDim);
    };

    return (
        <Paper className={classes.usageCard} elevation={0}>
            <div
                role="button"
                tabIndex={0}
                aria-expanded={open}
                className={classes.usageHead + (open ? ' ' + classes.usageHeadOpen : '')}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <div className={classes.usageTitleWrap}>
                    <InsertChartOutlinedIcon style={{ color: BRAND_COLOR, fontSize: 21 }} />
                    <div>
                        <div className={classes.usageTitle}>Most-Used Materials</div>
                        <div className={classes.usageSub}>
                            Top {top.length} of {data.movedCount} items that moved in the last {monthLabel}
                        </div>
                    </div>
                </div>

                <div className={classes.usageHeadRight}>
                    {/* closed, the header still has to say something useful */}
                    <span className={classes.usageHint}>
                        {open
                            ? 'Click a row for its month-by-month split'
                            : formatQty(totalQty) + ' outwarded · ' + share(topQty).toFixed(1) + '% from the top ' + top.length}
                    </span>
                    <ExpandMoreIcon
                        className={classes.usageChevron + (open ? ' ' + classes.usageChevronOpen : '')}
                    />
                </div>
            </div>

            <Collapse in={open} timeout={260} unmountOnExit>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <div className={classes.usageHero}>
                        <UsageDonut
                            data={data}
                            hoverIndex={hoverIndex}
                            onHoverSlice={setHoverIndex}
                            classes={classes}
                        />
                        <UsageDonutDetail data={data} hoverIndex={hoverIndex} classes={classes} />
                    </div>
                </Grid>

                <Grid item xs={12} md={8}>
                    {top.map((row, index) => (
                        <ExplainValueCell
                            key={row._id}
                            classes={classes}
                            badgeClass={classes.usageRow + rowTone(index)}
                            panel={(
                                <ExplainPanel title={row.itemName} classes={classes}>
                                    <PanelTable
                                        head={['Month', 'Qty Outwarded']}
                                        rows={months.map((month) => ({
                                            key: month.key,
                                            cells: [month.label, formatQty(row['month_' + month.key])],
                                        }))}
                                        classes={classes}
                                    />
                                    <PanelFormula
                                        label="Total"
                                        expr={'Rank ' + (index + 1) + ' · ' + share(row.totalOutwardedQty).toFixed(1) + '% of all qty outwarded'}
                                        subbed={months.map((month) => formatQty(row['month_' + month.key])).join(' + ')}
                                        result={formatQty(row.totalOutwardedQty)}
                                        classes={classes}
                                    />
                                </ExplainPanel>
                            )}
                            value={(
                                <>
                                    <span className={classes.usageRank + (index === 0 ? ' ' + classes.usageRankTop : '')}>
                                        {index + 1}
                                    </span>
                                    <span className={classes.usageNameWrap}>
                                        <span
                                            className={classes.usageSwatch}
                                            style={{ background: sliceColor(index) }}
                                        />
                                        <span className={classes.usageName} title={row.itemName}>{row.itemName}</span>
                                    </span>
                                    <span className={classes.usageTrack}>
                                        <span
                                            className={classes.usageFill}
                                            style={{
                                                width: Math.max(3, (row.totalOutwardedQty / peak) * 100) + '%',
                                                background: sliceColor(index),
                                            }}
                                        />
                                    </span>
                                    <span className={classes.usageQty}>{formatQty(row.totalOutwardedQty)}</span>
                                    <span className={classes.usageShare}>{share(row.totalOutwardedQty).toFixed(1)}%</span>
                                </>
                            )}
                        />
                    ))}
                </Grid>
            </Grid>
            </Collapse>
        </Paper>
    );
};


const MoqValueCell = React.memo(({ row, months, classes }) => {
    if (!months.length) return <span className={classes.numStrong}>{row.moq}</span>;

    const monthQty = months.map((month) => row['month_' + month.key]);

    const panel = (
        <ExplainPanel title={'Qty outwarded — last ' + months.length + ' months'} classes={classes}>
            <PanelTable
                head={['Month', 'Qty Outwarded']}
                rows={months.map((month) => ({
                    key: month.key,
                    cells: [month.label, row['month_' + month.key]],
                }))}
                classes={classes}
            />
            <PanelFormula
                expr={'(' + months.map((month) => month.label).join(' + ') + ') ÷ ' + months.length}
                subbed={'(' + monthQty.join(' + ') + ') ÷ ' + months.length}
                result={row.moq}
                classes={classes}
            />
        </ExplainPanel>
    );

    return <ExplainValueCell value={row.moq} panel={panel} classes={classes} />;
});



const ShippingTatCell = React.memo(({ row, classes, onSave }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    const days = Number(row.shippingTat) || 0;

    const openEditor = (event) => {
        setDraft(String(days));
        setAnchorEl(event.currentTarget);
    };
    const closeEditor = () => {
        if (saving) return;
        setAnchorEl(null);
        setDraft('');
    };

    const valid = isValidTatDraft(draft);
    const canSave = valid && !saving && Number(draft) !== days;

    const commit = async () => {
        if (!canSave) return;
        setSaving(true);
        const ok = await onSave(row, Number(draft));
        setSaving(false);
        if (ok) {
            setAnchorEl(null);
            setDraft('');
        }
    };

    return (
        <>
            <ExplainTooltip
                classes={classes}
                title={<div className={classes.tipText}>
                    Shipping TAT for this item — <span className={classes.tipTextStrong}>click to edit</span>
                </div>}
            >
                <span
                    role="button"
                    tabIndex={0}
                    className={classes.tatValue + (days ? '' : ' ' + classes.tatEmpty)}
                    onClick={openEditor}
                    onKeyPress={(event) => { if (event.key === 'Enter') openEditor(event); }}
                >
                    {days}
                </span>
            </ExplainTooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={closeEditor}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                classes={{ paper: classes.moqPopoverPaper }}
            >
                <div className={classes.tatPopover}>
                    <div className={classes.tatPopoverTitle}>Shipping TAT</div>
                    <div className={classes.tatPopoverMeta}>
                        {row.itemName} · currently {days} day{days === 1 ? '' : 's'}
                    </div>

                    <input
                        autoFocus
                        type="number"
                        min={0}
                        max={MAX_TAT_DAYS}
                        step={1}
                        inputMode="numeric"
                        className={classes.tatInput}
                        aria-label="Shipping TAT in days"
                        placeholder="Days"
                        value={draft}
                        onChange={(event) => {
                            const next = event.target.value;
                            if (next === '' || /^\d+$/.test(next)) setDraft(next);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') commit();
                            if (event.key === 'Escape') closeEditor();
                        }}
                    />

                    <div className={classes.tatPreview}>
                        {draft === '' ? 'Enter the number of days' : null}
                        {draft !== '' && !valid ? 'Enter a whole number from 0 to ' + MAX_TAT_DAYS : null}
                        {valid ? (
                            <>Shipping TAT <span className={classes.tatPreviewDays}>{Number(draft)} day{Number(draft) === 1 ? '' : 's'}</span></>
                        ) : null}
                    </div>

                    <div className={classes.tatActions}>
                        <button type="button" className={classes.tatCancel} onClick={closeEditor} disabled={saving}>
                            Cancel
                        </button>
                        <button type="button" className={classes.tatSave} disabled={!canSave} onClick={commit}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </Popover>
        </>
    );
});


const TotalAvailableCell = React.memo(({ row, classes }) => (
    <ExplainValueCell
        value={row.totalAvailableQty}
        classes={classes}
        panel={(
            <ExplainPanel title="Total Avl + Ordered Qty" classes={classes}>
                <PanelTable
                    head={['Component', 'Qty']}
                    rows={[
                        { key: 'stock', cells: ['Currently Available Stock', row.availableStock] },
                        { key: 'kanishka', cells: ['Open PO Pending with Kanishka', row.pendingApprovalQty] },
                        { key: 'vendor', cells: ['Open PO Pending with Vendor', row.pendingVendorQty] },
                    ]}
                    classes={classes}
                />
                <PanelFormula
                    expr="Available Stock + Open PO with Kanishka + Open PO with Vendor"
                    subbed={row.availableStock + ' + ' + row.pendingApprovalQty + ' + ' + row.pendingVendorQty}
                    result={row.totalAvailableQty}
                    classes={classes}
                />
            </ExplainPanel>
        )}
    />
));


const CompareQtyCell = React.memo(({ row, classes }) => (
    <ExplainValueCell
        value={row.compareQty}
        classes={classes}
        panel={(
            <ExplainPanel title="Qty to be Compared" classes={classes}>
                <PanelTable
                    head={['Component', 'Value']}
                    rows={[
                        { key: 'moq', cells: ['MOQ (avg qty outwarded)', row.moq] },
                        { key: 'tat', cells: ['Shipping TAT in days', row.shippingTat] },
                    ]}
                    classes={classes}
                />
                <PanelFormula
                    expr="MOQ + (MOQ × Shipping TAT ÷ 30)"
                    subbed={row.moq + ' + (' + row.moq + ' × ' + row.shippingTat + ' ÷ 30)'}
                    result={row.compareQty}
                    classes={classes}
                />
                <div className={classes.panelFoot}>
                    One MOQ, plus however much of an MOQ the shipping TAT consumes — a month counted as 30 days.
                </div>
            </ExplainPanel>
        )}
    />
));



const TriggerReadyCell = React.memo(({ row, classes }) => {
    const ready = Boolean(row.triggerReady);
    const Icon = ready ? CheckCircleIcon : RemoveCircleOutlineIcon;
    const label = ready ? 'Ready' : 'Not Ready';

    const panel = (
        <ExplainPanel title="Qty to Trigger Open PO" classes={classes}>
            <PanelTable
                head={['Column', 'Value']}
                rows={[
                    { key: 'total', cells: ['Total Avl + Ordered Qty', row.totalAvailableQty] },
                    { key: 'compare', cells: ['Qty to be Compared', row.compareQty] },
                ]}
                classes={classes}
            />
            <PanelFormula
                label="Condition"
                expr="(Total Avl + Ordered Qty) ≤ (Qty to be Compared)"
                subbed={'(' + row.totalAvailableQty + ') ≤ (' + row.compareQty + ')'}
                result={label}
                resultTone={ready ? 'yes' : 'no'}
                classes={classes}
            />
            <div className={classes.panelVerdict + ' ' + (ready ? classes.panelVerdictYes : classes.panelVerdictNo)}>
                <Icon />
                {ready
                    ? 'Ready — stock on hand and on order no longer covers the re-order line'
                    : 'Not Ready — stock on hand and on order still covers the re-order line'}
            </div>
            {row.compareQty === 0 ? (
                <div className={classes.panelFoot}>
                    This item has no MOQ, so there is no re-order line to fall below.
                </div>
            ) : null}
        </ExplainPanel>
    );

    return (
        <ExplainValueCell
            classes={classes}
            panel={panel}
            badgeClass={classes.readyBadge + ' ' + (ready ? classes.readyYes : classes.readyNo)}
            value={<><Icon />{label}</>}
        />
    );
});


const TriggerStatusCell = React.memo(({ row, classes }) => {
    if (!row.triggeredAt) return <span className={classes.muted}>—</span>;

    const panel = (
        <ExplainPanel title="Open PO triggered" classes={classes}>
            <PanelTable
                head={['Detail', 'Value']}
                rows={[
                    { key: 'order', cells: ['Order ID', row.triggerOrderId || '—'] },
                    { key: 'at', cells: ['Triggered at', formatDateTime(row.triggeredAt)] },
                    { key: 'status', cells: ['PO status', row.triggerPoStatus || '—'] },
                ]}
                classes={classes}
            />
            <div className={classes.panelFoot}>
                This stays until the PO is Delivered, then the column clears.
            </div>
        </ExplainPanel>
    );

    return (
        <ExplainValueCell
            classes={classes}
            panel={panel}
            badgeClass={classes.triggeredBadge}
            value={<>Triggered at<br />{formatDateTime(row.triggeredAt)}</>}
        />
    );
});


const OpenPoQtyCell = React.memo(({ row, col, classes }) => {
    const qty = row[col.qtyKey] || 0;
    const lines = row[col.linesKey] || [];

    if (!qty) return <span className={classes.muted}>0</span>;

    const shown = lines.slice(0, OPEN_PO_TIP_LIMIT);

    const panel = (
        <ExplainPanel
            title={lines.length + ' open PO' + (lines.length === 1 ? '' : 's') + ' ' + col.tipLabel}
            classes={classes}
        >
            <PanelTable
                head={['PO Number', 'Qty', 'Raised']}
                rows={shown.map((line) => ({
                    key: String(line.materialLineId),
                    cells: [line.orderId || 'PO', line.quantity, formatDate(line.poCreatedAt)],
                }))}
                classes={classes}
            />
            <PanelFormula
                label="Total"
                expr={'Sum of all ' + lines.length + ' open PO qty'}
                subbed={shown.map((line) => line.quantity).join(' + ')
                    + (lines.length > shown.length ? ' + … ' + (lines.length - shown.length) + ' more' : '')}
                result={qty}
                classes={classes}
            />
        </ExplainPanel>
    );

    return (
        <ExplainValueCell value={qty} panel={panel} classes={classes} badgeClass={classes.pendingQty} />
    );
});



const MoqTableRow = React.memo(({ row, serial, index, classes, columns, months, onSaveShippingTat }) => (
    <TableRow className={classes.bodyRow} style={{ animationDelay: Math.min(index, 12) * 28 + 'ms' }}>
        {columns.map((col) => {
            const stickyStyle = col.sticky
                ? { left: col.stickyLeft, width: col.width, minWidth: col.width, maxWidth: col.width }
                : undefined;
            const cellClass = classes.bodyCell
                + (col.sticky ? ' ' + classes.stickyCell : '')
                + (col.lastSticky ? ' ' + classes.stickyEdge : '');
            let content = null;

            if (col.pending) {
                return (
                    <TableCell key={col.id} className={cellClass} style={stickyStyle} align={col.align}>
                        <span className={classes.muted}>—</span>
                    </TableCell>
                );
            }

            switch (col.type) {
                case 'index':
                    content = <span className={classes.serial}>{serial}</span>;
                    break;
                case 'text':
                    content = (
                        <ExplainTooltip
                            classes={classes}
                            placement="top-start"
                            title={<div className={classes.tipText}>
                                <span className={classes.tipTextStrong}>{row.itemName}</span>
                                {' • ' + row.itemCode}
                            </div>}
                        >
                            <div className={classes.itemCell}>
                                <div className={classes.itemName}>{row.itemName}</div>
                                <div className={classes.itemMeta}>{row.itemCode} • {row.category}</div>
                            </div>
                        </ExplainTooltip>
                    );
                    break;
                case 'moq':
                    content = <MoqValueCell row={row} months={months} classes={classes} />;
                    break;
                case 'shippingTat':
                    content = <ShippingTatCell row={row} classes={classes} onSave={onSaveShippingTat} />;
                    break;
                case 'openPoQty':
                    content = <OpenPoQtyCell row={row} col={col} classes={classes} />;
                    break;
                case 'totalAvailableQty':
                    content = <TotalAvailableCell row={row} classes={classes} />;
                    break;
                case 'compareQty':
                    content = <CompareQtyCell row={row} classes={classes} />;
                    break;
                case 'triggerReady':
                    content = <TriggerReadyCell row={row} classes={classes} />;
                    break;
                case 'triggerStatus':
                    content = <TriggerStatusCell row={row} classes={classes} />;
                    break;
                case 'trigger':
                    content = <TriggerChip value={row.triggerStatus} classes={classes} className={classes.statusChip} />;
                    break;
                case 'poStatus':
                    content = <PoStatusChip value={row.poStatus} classes={classes} className={classes.statusChip} />;
                    break;
                case 'stage':
                    content = <StageChip stage={row.stage} classes={classes} className={classes.stageChip} />;
                    break;
                case 'datetime':
                    content = row.triggerDateTime
                        ? <span className={classes.dateText}>{formatDateTime(row.triggerDateTime)}</span>
                        : <span className={classes.muted}>—</span>;
                    break;
                case 'date':
                    content = row.sentToVendorDate
                        ? <span className={classes.dateText}>{formatDate(row.sentToVendorDate)}</span>
                        : <span className={classes.muted}>—</span>;
                    break;
                default: {
                    const isLow = col.id === 'availableStock' && row.belowMoq;
                    content = <span className={isLow ? classes.lowStock : classes.numStrong}>{row[col.id]}</span>;
                }
            }

            return (
                <TableCell key={col.id} className={cellClass} style={stickyStyle} align={col.align}>
                    {content}
                </TableCell>
            );
        })}
    </TableRow>
));


const MoqMobileCard = React.memo(({ row, index, classes, months }) => (
    <Paper className={classes.mCard} elevation={0} style={{ animationDelay: Math.min(index, 10) * 40 + 'ms' }}>
        <div className={classes.mHead}>
            <div style={{ minWidth: 0 }}>
                <div className={classes.mItemName}>{row.itemName}</div>
                <div className={classes.mItemMeta}>{row.itemCode}{row.category ? ' • ' + row.category : ''}</div>
            </div>
            {row.belowMoq && (
                <Chip size="small" className={classes.statusChip} style={{ backgroundColor: TONES.rose.bg, color: TONES.rose.color, border: '1px solid ' + TONES.rose.border }} label="Below MOQ" />
            )}
        </div>

        <div className={classes.mGrid}>
            <div>
                <div className={classes.mLabel}>Stock</div>
                <div className={classes.mValue} style={row.belowMoq ? { color: '#c62828' } : undefined}>
                    {row.availableStock}
                </div>
            </div>
            <div>
                <div className={classes.mLabel}>MOQ</div>
                <div className={classes.mValue}>{row.moq}</div>
            </div>
            <div>
                <div className={classes.mLabel}>Outwarded</div>
                <div className={classes.mValue}>{row.totalOutwardedQty}</div>
            </div>
            {months.map((month) => (
                <div key={month.key}>
                    <div className={classes.mLabel}>{month.label}</div>
                    <div className={classes.mValue}>{row['month_' + month.key]}</div>
                </div>
            ))}
        </div>

        <div className={classes.mFoot}>
            <span className={classes.mItemMeta}>Unit · {row.unit || '—'}</span>
            <span className={classes.mItemMeta}>Consumed · {row.consumedStock}</span>
        </div>
    </Paper>
));


const MonAndOrders = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [rawData, setRawData] = useState([]);
    const [months, setMonths] = useState([]);
    const [fetching, setFetching] = useState(true);
    const booting = useDecorpotLoader(BOOT_DELAY);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [order, setOrder] = useState({ by: '', dir: 'asc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const { setSnackInfo } = props;

    const fetchMoqOrders = useCallback(async (showSnack) => {
        setFetching(true);
        try {
            const response = await HttpService.getMoqAndOrders({ type: SHEET_TYPE, months: SHEET_MONTHS });
            const payload = response.data || {};
            setMonths(payload.meta?.months || []);
            setRawData(Array.isArray(payload.data) ? payload.data : []);
            if (showSnack) setSnackInfo((payload.data || []).length + ' item(s) refreshed', 'success');
        } catch (error) {
            console.error('Failed to load MOQ & open PO data', error);
            setMonths([]);
            setRawData([]);
            setSnackInfo(error?.response?.data?.message || 'Unable to load MOQ & open PO data', 'error');
        }
        setFetching(false);
    }, [setSnackInfo]);

    useEffect(() => {
        fetchMoqOrders(false);
    }, [fetchMoqOrders]);

    const columns = useMemo(() => buildColumns(months), [months]);
    const rows = useMemo(() => rawData.map((row) => buildRow(row, months)), [rawData, months]);

    /* filtering */
    const filteredRows = useMemo(() => {
        const term = filters.search.trim().toLowerCase();

        return rows.filter((row) => {
            if (term) {
                const haystack = (row.itemName + ' ' + (row.itemCode || '') + ' ' + (row.category || '')).toLowerCase();
                if (!haystack.includes(term)) return false;
            }

            if (filters.view === VIEWS.ALL && row.moq === 0) return false;
            if (filters.view === VIEWS.ZERO_MOQ && row.moq !== 0) return false;
            if (filters.view === VIEWS.BELOW_MOQ && !row.belowMoq) return false;
            if (filters.view === VIEWS.OUTWARDED && !(row.totalOutwardedQty > 0)) return false;
            if (filters.view === VIEWS.ZERO_STOCK && row.availableStock !== 0) return false;
            return true;
        });
    }, [rows, filters]);

    /* sorting */
    const sortedRows = useMemo(() => {
        if (!order.by) return filteredRows;
        const factor = order.dir === 'asc' ? 1 : -1;
        return [...filteredRows].sort((a, b) => factor * comparator(a, b, order.by));
    }, [filteredRows, order]);

    const pagedRows = useMemo(
        () => (rowsPerPage === ALL_ROWS
            ? sortedRows
            : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [sortedRows, page, rowsPerPage],
    );

    const serialOffset = rowsPerPage === ALL_ROWS ? 0 : page * rowsPerPage;


    const topMaterials = useMemo(() => {
        const moved = rows.filter((row) => row.totalOutwardedQty > 0);
        const top = [...moved]
            .sort((a, b) => b.totalOutwardedQty - a.totalOutwardedQty)
            .slice(0, TOP_MATERIALS_COUNT);

        const totalQty = moved.reduce((sum, row) => sum + row.totalOutwardedQty, 0);
        const topQty = top.reduce((sum, row) => sum + row.totalOutwardedQty, 0);

        const sliced = top.slice(0, DONUT_SLICE_COUNT);
        const slices = sliced.map((row, index) => ({
            key: String(row._id),
            label: row.itemName,
            qty: row.totalOutwardedQty,
            color: SLICE_COLORS[index],
        }));

        const otherQty = totalQty - sliced.reduce((sum, row) => sum + row.totalOutwardedQty, 0);
        const otherCount = moved.length - sliced.length;
        if (otherQty > 0 && otherCount > 0) {
            slices.push({
                key: 'other',
                label: 'Other (' + otherCount + ' item' + (otherCount === 1 ? '' : 's') + ')',
                qty: Math.round(otherQty * 100) / 100,
                color: OTHER_COLOR,
            });
        }

        return {
            top: top,
            slices: slices,
            movedCount: moved.length,
            totalQty: totalQty,
            topQty: topQty,
        };
    }, [rows]);

    const stats = useMemo(() => ({
        total: rows.length,
        belowMoq: rows.filter((r) => r.belowMoq).length,
        outwarded: rows.filter((r) => r.totalOutwardedQty > 0).length,
        zeroStock: rows.filter((r) => r.availableStock === 0).length,
    }), [rows]);

    const hasActiveFilters = filters.search !== '' || filters.view !== VIEWS.ALL;

    /* ---------- handlers ---------- */
    const updateFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(0);
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        setOrder({ by: '', dir: 'asc' });
        setPage(0);
        setSnackInfo('All filters cleared', 'success');
    }, [setSnackInfo]);


    const saveShippingTat = useCallback(async (row, tatDays) => {
        try {
            const response = await HttpService.updateMoqShippingTat({
                materialId: row._id,
                tatDays: tatDays,
                type: SHEET_TYPE,
                months: SHEET_MONTHS,
            });
            const saved = response.data?.data || {};
            const savedDays = Number(saved.moqTaTDays) || 0;

            setRawData((prev) => prev.map((item) => (String(item._id) === String(row._id)
                ? {
                    ...item,
                    shippingTat: savedDays,
                    totalAvailableQty: Number(saved.totalAvailableQty) || 0,
                    compareQty: Number(saved.compareQty) || 0,
                    triggerReady: Boolean(saved.triggerReady),
                }
                : item)));

            setSnackInfo('Shipping TAT saved — ' + savedDays + ' day(s) for ' + row.itemName, 'success');
            return true;
        } catch (error) {
            console.error('Failed to save shipping TAT', error);
            setSnackInfo(error?.response?.data?.message || 'Unable to save shipping TAT', 'error');
            return false;
        }
    }, [setSnackInfo]);

    const handleSort = useCallback((columnId) => {
        setOrder((prev) => ({
            by: columnId,
            dir: prev.by === columnId && prev.dir === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    const downloadCsv = useCallback(() => {
        if (!sortedRows.length) {
            props.setSnackInfo('No records to download', 'warning');
            return;
        }
        props.setLoading(true);
        try {
            const headers = ['Sl No', 'Item Name', 'Item Code', 'Category', 'Unit',
                'Currently Available Stock', 'Consumed Qty',
                ...months.map((month) => month.label + ' Qty'),
                'Total Qty (' + months.length + ' months)', 'MOQ (avg qty outwarded)', 'Below MOQ',
                'Shipping TAT (days)', 'Open PO Pending with Kanishka', 'Open PO Pending with Vendor',
                'Total Avl + Ordered Qty', 'Qty to be Compared', 'Qty to Trigger Open PO'];

            const lines = sortedRows.map((row, index) => [
                index + 1, row.itemName, row.itemCode, row.category, row.unit,
                row.availableStock, row.consumedStock,
                ...months.map((month) => row['month_' + month.key]),
                row.totalOutwardedQty, row.moq, row.belowMoq ? 'Yes' : 'No',
                row.shippingTat, row.pendingApprovalQty, row.pendingVendorQty,
                row.totalAvailableQty, row.compareQty, row.triggerReady ? 'Ready' : 'Not Ready',
            ].map(escapeCsv).join(','));

            const csv = '﻿' + [headers.map(escapeCsv).join(','), ...lines].join('\r\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'MOQ_And_Orders_' + moment().format('DD-MM-YYYY_HH-mm') + '.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            props.setSnackInfo(sortedRows.length + ' record(s) downloaded', 'success');
        } catch (error) {
            console.error('CSV download failed', error);
            props.setSnackInfo('Unable to download CSV', 'error');
        }
        props.setLoading(false);
    }, [sortedRows, months, props]);

    /* ---------- loader ---------- */
    if (booting || (fetching && !rawData.length)) {
        return (
            <div className={classes.page}>
                <DecorpotLoader
                    text="Preparing MOQ & Open PO sheet"
                    subText="Pulling live factory stock and the last 3 completed months of delivered qty"
                />
            </div>
        );
    }

    /* ---------- render ---------- */
    return (
        <Fade in timeout={420}>
            <div className={classes.page}>
                {/* Header */}
                <div className={classes.header}>
                    <div className={classes.titleWrap}>
                        <div className={classes.titleIcon}><LayersIcon style={{ fontSize: 28 }} /></div>
                        <Typography className={classes.title}>MOQ &amp; Auto Open PO Orders</Typography>
                    </div>

                    {/* on small screens the same two actions live inside the filter card */}
                    {!isMobile && (
                        <div className={classes.headerActions}>
                            <Button
                                className={classes.ghostBtn}
                                startIcon={<RefreshIcon />}
                                onClick={() => fetchMoqOrders(true)}
                                disabled={fetching}
                            >
                                {fetching ? 'Refreshing…' : 'Refresh'}
                            </Button>
                            <Button
                                className={classes.ghostBtn}
                                startIcon={<ClearIcon />}
                                onClick={resetFilters}
                                disabled={!hasActiveFilters && !order.by}
                            >
                                Reset Filters
                            </Button>
                            <Button className={classes.primaryBtn} startIcon={<GetAppIcon />} onClick={downloadCsv}>
                                Download CSV
                            </Button>
                        </div>
                    )}
                </div>

                {/* KPIs */}
                <Grid container spacing={3}>
                    <Grid item xs={6} sm={6} md={3}>
                        <KpiCard classes={classes} delay="0ms" icon={<LayersIcon />} value={stats.total} label="Total Items" color={BRAND_COLOR} bg={BRAND_SOFT} />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                        <KpiCard classes={classes} delay="60ms" icon={<ErrorOutlineIcon />} value={stats.belowMoq} label="Stock at / below MOQ" color="#c62b2b" bg="#ffecec" />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                        <KpiCard classes={classes} delay="120ms" icon={<LocalShippingIcon />} value={stats.outwarded} label={'Outwarded in ' + (months.length || 3) + ' months'} color="#0f8a5f" bg="#e2f5ee" />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                        <KpiCard classes={classes} delay="180ms" icon={<HourglassEmptyIcon />} value={stats.zeroStock} label="Zero Stock Items" color="#c98a00" bg="#fff1d6" />
                    </Grid>
                </Grid>

                {/* Most-used materials — sits above the filters, reports on the whole sheet */}
                <div className={classes.rise} style={{ animationDelay: '200ms', marginTop: 24 }}>
                    <TopMaterialsCard data={topMaterials} months={months} classes={classes} />
                </div>

                {/* Filters */}
                <Paper className={classes.filterCard + ' ' + classes.rise} elevation={0} style={{ animationDelay: '260ms' }}>
                    <div className={classes.filterHead}>
                        <FilterListIcon style={{ color: BRAND_COLOR, fontSize: 22 }} />
                        <span className={classes.filterHeadText}>Filters</span>
                    </div>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                className={classes.field}
                                placeholder="Search item name, code or category…"
                                value={filters.search}
                                onChange={(e) => updateFilter('search', e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon className={classes.adornIcon} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: filters.search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => updateFilter('search', '')}>
                                                <ClearIcon style={{ fontSize: 18 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
                                <InputLabel id="moq-view-label">Show</InputLabel>
                                <Select
                                    labelId="moq-view-label"
                                    label="Show"
                                    value={filters.view}
                                    onChange={(e) => updateFilter('view', e.target.value)}
                                >
                                    {VIEW_LIST.map((view) => (
                                        <MenuItem key={view} value={view}>{view}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {isMobile && (
                            <Grid item xs={12}>
                                <Box display="flex" gridGap={12}>
                                    <Button fullWidth className={classes.ghostBtn} startIcon={<RefreshIcon />} onClick={() => fetchMoqOrders(true)} disabled={fetching}>
                                        Refresh
                                    </Button>
                                    <Button fullWidth className={classes.ghostBtn} startIcon={<ClearIcon />} onClick={resetFilters}>
                                        Reset
                                    </Button>
                                    <Button fullWidth className={classes.primaryBtn} startIcon={<GetAppIcon />} onClick={downloadCsv}>
                                        CSV
                                    </Button>
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    {hasActiveFilters && (
                        <div className={classes.activeChips}>
                            {filters.search && (
                                <Chip size="small" className={classes.activeChip} label={'Search: ' + filters.search} onDelete={() => updateFilter('search', '')} />
                            )}
                            {filters.view !== VIEWS.ALL && (
                                <Chip size="small" className={classes.activeChip} label={'Show: ' + filters.view} onDelete={() => updateFilter('view', VIEWS.ALL)} />
                            )}
                        </div>
                    )}
                </Paper>

                {/* Data */}
                {isMobile ? (
                    <Box mt={3}>
                        {pagedRows.length === 0 ? (
                            <Paper className={classes.tableCard} elevation={0}>
                                <div className={classes.empty}>
                                    <InboxIcon className={classes.emptyIcon} />
                                    <div className={classes.emptyTitle}>No matching records</div>
                                    <div className={classes.emptyText}>Try changing the search or the Show filter.</div>
                                </div>
                            </Paper>
                        ) : (
                            pagedRows.map((row, index) => (
                                <MoqMobileCard key={row._id} row={row} index={index} classes={classes} months={months} />
                            ))
                        )}
                        <Paper elevation={0} style={{ borderRadius: 16, border: '1px solid #ebeef4' }}>
                            <TablePagination
                                className={classes.pagination}
                                component="div"
                                count={sortedRows.length}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                labelDisplayedRows={displayedRowsLabel(rowsPerPage)}
                            />
                        </Paper>
                    </Box>
                ) : (
                    <Paper className={classes.tableCard + ' ' + classes.fadeUp} elevation={0} style={{ animationDelay: '280ms' }}>
                        <div className={classes.tableToolbar}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span className={classes.tableTitle}>Open PO Trigger Sheet</span>
                                <span className={classes.countPill}>{sortedRows.length} records</span>
                            </div>
                            <span className={classes.toolbarHint}>
                                {months.length
                                    ? 'MOQ = average qty outwarded across ' + months.map((m) => m.label).join(', ')
                                    : 'Hover a column header for how it is calculated'}
                            </span>
                        </div>

                        <TableContainer className={classes.tableWrap}>
                            <Table stickyHeader size="small" className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        {columns.map((col) => {
                                            const tone = col.tone ? TONE_STYLES[col.tone] : null;
                                            return (
                                                <TableCell
                                                    key={col.id}
                                                    align={col.align}
                                                    className={
                                                        classes.headCell + ' ' + classes.headCellHover
                                                        + (col.sticky ? ' ' + classes.stickyCell + ' ' + classes.stickyHeadCell : '')
                                                        + (col.lastSticky ? ' ' + classes.stickyEdge : '')
                                                    }
                                                    style={{
                                                        minWidth: col.width,
                                                        width: col.width,
                                                        maxWidth: col.sticky ? col.width : undefined,
                                                        left: col.sticky ? col.stickyLeft : 'auto',
                                                        backgroundColor: tone ? tone.bg : '#fbfcfe',
                                                    }}
                                                    sortDirection={order.by === sortKeyOf(col) ? order.dir : false}
                                                >
                                                    <ExplainTooltip
                                                        classes={classes}
                                                        title={col.note
                                                            ? <div className={classes.tipText}>{col.note}</div>
                                                            : ''}
                                                    >
                                                        <span className={classes.headInner}>
                                                            {col.sortable === false ? (
                                                                <span className={classes.headLabel}>{col.label}</span>
                                                            ) : (
                                                                <TableSortLabel
                                                                    className={classes.sortLabel}
                                                                    active={order.by === sortKeyOf(col)}
                                                                    direction={order.by === sortKeyOf(col) ? order.dir : 'asc'}
                                                                    onClick={() => handleSort(sortKeyOf(col))}
                                                                >
                                                                    <span className={classes.headLabel}>{col.label}</span>
                                                                </TableSortLabel>
                                                            )}
                                                            {col.note && <InfoOutlinedIcon className={classes.headInfo} />}
                                                        </span>
                                                    </ExplainTooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {pagedRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} style={{ borderBottom: 'none' }}>
                                                <div className={classes.empty}>
                                                    <InboxIcon className={classes.emptyIcon} />
                                                    <div className={classes.emptyTitle}>No matching records</div>
                                                    <div className={classes.emptyText}>Try changing the search or the Show filter.</div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        pagedRows.map((row, index) => (
                                            <MoqTableRow
                                                key={row._id}
                                                row={row}
                                                index={index}
                                                serial={serialOffset + index + 1}
                                                classes={classes}
                                                columns={columns}
                                                months={months}
                                                onSaveShippingTat={saveShippingTat}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            className={classes.pagination}
                            component="div"
                            count={sortedRows.length}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelDisplayedRows={displayedRowsLabel(rowsPerPage)}
                        />
                    </Paper>
                )}
            </div>
        </Fade>
    );
};

export default connect(null, { setLoading, setSnackInfo })(MonAndOrders);
